// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useRef } from 'react';
import { useFirmwareUpdate } from '../context/FirmwareUpdateContext';
import { useAlerts } from '../context/AlertsContext';
import { useStargates } from '../context/StargatesContext';

const ALERT_ID = 'firmware_update';
const ACTIVE_STATUSES = new Set([
  'pending',
  'initializing',
  'uploading',
  'updating',
  'completing',
  'rebooting',
  'retrying',
]);
const FAILURE_STATUSES = new Set(['error', 'failed', 'cancelled', 'timeout']);

function getStargateName(stargates, stargateId) {
  const id = typeof stargateId === 'string' ? parseInt(stargateId, 10) : stargateId;
  const sg = stargates?.find((s) => s.id === id);
  return sg?.friendly_name || sg?.serial_number || sg?.name || `Stargate #${stargateId}`;
}

export default function FirmwareUpdateAlertsBridge() {
  const { deploymentStatuses } = useFirmwareUpdate();
  const { stargates } = useStargates();
  const { upsertAlert, removeAlert } = useAlerts();
  const hadActiveRef = useRef(false);

  useEffect(() => {
    const entries = Object.entries(deploymentStatuses || {});
    const inProgress = [];
    const failed = [];

    for (const [sgId, s] of entries) {
      if (!s || !s.status) continue;
      const name = getStargateName(stargates, sgId);
      if (ACTIVE_STATUSES.has(s.status)) {
        const pct = s.progress != null ? ` ${Math.round(s.progress)}%` : '';
        inProgress.push(`${name}: ${s.status}${pct}`);
      } else if (FAILURE_STATUSES.has(s.status)) {
        failed.push(`${name}: ${s.message || s.status}`);
      }
    }

    const hasInProgress = inProgress.length > 0;
    const hasFailed = failed.length > 0;

    if (hasInProgress) {
      hadActiveRef.current = true;
      let severity = hasFailed ? 'warning' : 'info';
      let message = hasFailed
        ? `${inProgress.length} in progress, ${failed.length} failed — ${inProgress.slice(0, 2).join('; ')}${inProgress.length > 2 ? '…' : ''}`
        : inProgress.length === 1
          ? inProgress[0]
          : `${inProgress.length} device(s) — ${inProgress.slice(0, 2).join('; ')}${inProgress.length > 2 ? '…' : ''}`;

      upsertAlert({
        id: ALERT_ID,
        type: 'firmware_update',
        severity,
        title: 'Firmware Update',
        message,
        autoExpire: false,
      });
    } else if (hasFailed) {
      hadActiveRef.current = false;
      upsertAlert({
        id: ALERT_ID,
        type: 'firmware_update',
        severity: 'warning',
        title: 'Firmware Update Failed',
        message: failed.slice(0, 3).join('; '),
        autoExpire: false,
      });
    } else if (hadActiveRef.current) {
      hadActiveRef.current = false;
      // success alerts auto-expire (AlertsContext default) — good news
      // shouldn't sit in the active-alerts count forever
      upsertAlert({
        id: ALERT_ID,
        type: 'firmware_update',
        severity: 'success',
        title: 'Firmware Update Completed',
        message: 'All devices updated successfully',
      });
    } else {
      hadActiveRef.current = false;
      removeAlert(ALERT_ID);
    }
  }, [deploymentStatuses, stargates, upsertAlert, removeAlert]);

  return null;
}
